import React, { useMemo, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, MenuItem, Paper, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
  alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useSelector } from "react-redux";
import {
  useGetSignageUsersQuery,
  useCreateSignageUserMutation,
  useUpdateSignageUserMutation,
  useDeleteSignageUserMutation,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { ISignageUser } from "../../../Api/Interface/api.interface";
import { selectIsSuperAdmin, selectCurrentUser } from "../../../Store/Auth/AuthSelector";
import ActionButtons from "../../../Components/ui/ActionButtons";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useNotifier } from "../../../Core/Notifier";
import {
  NAVY, GOLD, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const ROLE_OPTIONS = [
  { v: "super_admin", label: "Super Admin", restrictedTo: "super_admin" },
  { v: "company_admin", label: "Company Admin" },
  { v: "content_manager", label: "Content Manager" },
];

interface IUserFormState {
  id?: number;
  name: string;
  username: string;
  email: string;
  password: string;
  role: string;
  company_id: number | "";
  is_active: boolean;
}

const emptyForm: IUserFormState = {
  name: "", username: "", email: "", password: "",
  role: "content_manager", company_id: "", is_active: true,
};

const SignageUsersList: React.FC<{ companyIdFilter?: number; embedded?: boolean }> = ({
  companyIdFilter, embedded = false,
}) => {
  const isSuper = useSelector(selectIsSuperAdmin);
  const currentUser = useSelector(selectCurrentUser);
  const { showMessage, showErrorMessage } = useNotifier();

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<IUserFormState | null>(null);
  const [toDelete, setToDelete] = useState<ISignageUser | null>(null);

  const queryArgs = useMemo(() => ({
    searchText: search || undefined,
    company_id: companyIdFilter || undefined,
  }), [search, companyIdFilter]);
  const { data, isLoading, refetch } = useGetSignageUsersQuery(queryArgs);
  const users: ISignageUser[] = Array.isArray(data?.data) ? (data?.data as unknown as ISignageUser[]) : [];

  const { data: companiesRes } = useGetCompanyQuery({ searchText: "", id: "" }, { skip: !isSuper });
  const companies: any[] = Array.isArray(companiesRes?.data) ? (companiesRes?.data as any[]) : [];

  const [createUser, { isLoading: isCreating }] = useCreateSignageUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateSignageUserMutation();
  const [deleteUser] = useDeleteSignageUserMutation();

  const openCreate = () => setEditing({ ...emptyForm, company_id: companyIdFilter ?? "" });
  const openEdit = (u: ISignageUser) => setEditing({
    id: u.id, name: u.name ?? "", username: u.username, email: u.email ?? "",
    password: "", role: u.role, company_id: u.company_id ?? "",
    is_active: u.is_active,
  });

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name || !editing.username) {
      showErrorMessage("Name and username are required");
      return;
    }
    if (!editing.id && !editing.password) {
      showErrorMessage("Password is required for new users");
      return;
    }
    const body: any = {
      name: editing.name.trim(),
      username: editing.username.trim(),
      email: editing.email.trim() || null,
      role: editing.role,
      is_active: editing.is_active,
    };
    if (editing.password) body.password = editing.password;
    if (editing.role !== "super_admin" && editing.company_id) {
      body.company_id = Number(editing.company_id);
    }

    try {
      const res = editing.id
        ? await updateUser({ id: editing.id, body }).unwrap()
        : await createUser(body).unwrap();
      if (res.status) {
        showMessage(editing.id ? "User updated" : "User created");
        setEditing(null);
        refetch();
      } else {
        showErrorMessage(res.message || "Save failed");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Save failed");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await deleteUser(toDelete.id).unwrap();
      if (res.status) {
        showMessage("User deleted");
        setToDelete(null);
        refetch();
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Delete failed");
    }
  };

  const roleChipColor = (role: string) => {
    if (role === "super_admin") return { bg: "#fef3c7", fg: "#92400e", bd: "#fcd34d" };
    if (role === "company_admin") return { bg: "#dbeafe", fg: "#1e40af", bd: "#bfdbfe" };
    return { bg: "#ede9fe", fg: "#5b21b6", bd: "#ddd6fe" };
  };

  if (isLoading) return <PageLoader height="20vh" />;

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.18)} 0%, ${alpha("#7c3aed", 0.08)} 100%)`,
            color: "#7c3aed",
          }}>
            <GroupIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
              Users
            </Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>
              Manage Super Admin, Company Admin and Content Manager accounts.
            </Typography>
          </Box>
        </Box>
      )}

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        <Box sx={{ display: "flex", gap: 1.5, p: 2, alignItems: "center" }}>
          <TextField size="small" placeholder="Search by name, username or email"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
            }}
            sx={{
              width: 360,
              "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" },
            }} />
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" disableElevation
            startIcon={<AddIcon />} onClick={openCreate}
            sx={{
              borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700,
              background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
              "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
            }}>
            Add User
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Name</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Username</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Role</TableCell>
                {isSuper && <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Company</TableCell>}
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</TableCell>
                <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuper ? 6 : 5} sx={{ textAlign: "center", py: 6, color: TEXT_SECONDARY }}>
                    No users yet.
                  </TableCell>
                </TableRow>
              ) : users.map((u) => {
                const color = roleChipColor(u.role);
                return (
                  <TableRow key={u.id} hover>
                    <TableCell sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                      {u.name || "—"}
                      {currentUser?.id === u.id && (
                        <Chip size="small" label="You" sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: "#ecfdf5", color: "#065f46", fontWeight: 700 }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: TEXT_PRIMARY }}>{u.username}</TableCell>
                    <TableCell>
                      <Chip size="small" label={u.role_label}
                        sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: color.bg, color: color.fg, border: `1px solid ${color.bd}` }} />
                    </TableCell>
                    {isSuper && (
                      <TableCell sx={{ fontSize: 13, color: TEXT_PRIMARY }}>
                        {u.company?.name || (u.role === "super_admin" ? "—" : "No company")}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip size="small" label={u.is_active ? "Active" : "Disabled"}
                        sx={{
                          height: 22, fontSize: 11, fontWeight: 700,
                          bgcolor: u.is_active ? "#dcfce7" : "#fee2e2",
                          color: u.is_active ? "#166534" : "#991b1b",
                        }} />
                    </TableCell>
                    <TableCell align="right">
                      <ActionButtons
                        onEdit={() => openEdit(u)}
                        onDelete={u.id === currentUser?.id ? undefined : () => setToDelete(u)}
                        deleteRequiresRole={["super_admin", "company_admin"]}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create / Edit dialog */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>
          {editing?.id ? "Edit User" : "Create User"}
        </DialogTitle>
        <DialogContent>
          {editing && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
              <TextField label="Full name" value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                size="small" fullWidth required />
              <TextField label="Username" value={editing.username}
                onChange={(e) => setEditing({ ...editing, username: e.target.value })}
                size="small" fullWidth required />
              <TextField label="Email" type="email" value={editing.email}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                size="small" fullWidth />
              <TextField label={editing.id ? "New password (optional)" : "Password"}
                type="password" value={editing.password}
                onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                size="small" fullWidth
                InputProps={editing.id ? {
                  startAdornment: <InputAdornment position="start"><LockResetIcon sx={{ fontSize: 16 }} /></InputAdornment>,
                } : undefined} />
              <TextField select label="Role" value={editing.role}
                onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                size="small" fullWidth>
                {ROLE_OPTIONS
                  .filter((opt) => !opt.restrictedTo || isSuper)
                  .map((opt) => (
                    <MenuItem key={opt.v} value={opt.v}>{opt.label}</MenuItem>
                  ))}
              </TextField>
              {isSuper && editing.role !== "super_admin" && (
                <TextField select label="Company" value={editing.company_id || ""}
                  onChange={(e) => setEditing({ ...editing, company_id: Number(e.target.value) || "" })}
                  size="small" fullWidth required>
                  <MenuItem value="">— Select —</MenuItem>
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>
                  ))}
                </TextField>
              )}
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`,
                bgcolor: editing.is_active ? alpha("#10b981", 0.05) : "#f8fafc",
                gridColumn: "span 2",
              }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>Active</Typography>
                  <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                    Disabled users cannot log in.
                  </Typography>
                </Box>
                <Switch checked={editing.is_active}
                  onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditing(null)}
            sx={{ textTransform: "none", borderRadius: 2, color: "#64748b", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation
            onClick={handleSave} disabled={isCreating || isUpdating}
            sx={{
              textTransform: "none", borderRadius: 2, fontWeight: 700,
              background: `linear-gradient(135deg, ${GOLD} 0%, #E5C158 100%)`,
              color: "#0b1e2d",
              "&:hover": { background: "linear-gradient(135deg, #C8A030 0%, #D4AF37 100%)" },
            }}>
            {editing?.id ? "Save Changes" : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeletePopup
        open={!!toDelete}
        handleCloseDelete={() => setToDelete(null)}
        onConfirm={handleDelete}
        title="Delete User"
        content={toDelete ? `Delete user "${toDelete.name || toDelete.username}"? This cannot be undone.` : ""}
      />
    </Box>
  );
};

export default SignageUsersList;
